/**
 * ROOM ALARMS panel (RELEASE-ALARMS.md §Rooms) — the body of the in-room ``Alarm (N)`` tab. Lists
 * this room group's TRIPPED live integration-test invariants (what broke, when, the last error) with
 * a per-alarm Reset. The tab only mounts when N > 0, so this always has ≥1 row. Reset re-arms the
 * alarm for the current release (clears the trip); once the fix bakes clean the alarm auto-untracks
 * and the tab disappears on its own.
 *
 * UPGRADE QUALITY (UPGRADE-QUALITY.md §6): a tripped ``upgrade-quality:<unit>`` alarm gets a COMPARE
 * block — the prior version's baseline vs the new output side by side, with the judge's verdict,
 * scores and evidence. Fetched lazily, only when such an alarm is present.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { fetchUpgradeQuality } from '@stepflow/ui-shared/services/dataPlaneApi';

const UQ_PREFIX = 'upgrade-quality:';

// SEGMENTS (RELEASE-ALARMS.md §4a.x): each alarm family is its own group, in this order. The PLAN
// segment (§4a.2 — planner / grooming / waves / gap agent) reads as "Plan lifecycle".
const SEGMENTS = [
  { key: 'plan', label: 'Plan lifecycle', match: (n) => n.startsWith('plan:') },
  { key: 'avatar', label: 'Agent container', match: (n) => n.startsWith('avatar:') },
  { key: 'turn', label: 'Turn integrity',
    match: (n) => n.startsWith('stream:') || n.startsWith('contract:') || n.startsWith('usage:') },
  { key: 'uq', label: 'Upgrade quality', match: (n) => n.startsWith(UQ_PREFIX) },
  { key: 'room', label: 'Room', match: () => true },
];

export function groupAlarmsBySegment(rows) {
  const groups = SEGMENTS.map((s) => ({ ...s, rows: [] }));
  for (const a of rows || []) {
    const n = String(a.Name || a.name || '');
    groups.find((g) => g.match(n)).rows.push(a);
  }
  return groups.filter((g) => g.rows.length);
}

function _when(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

function UpgradeCompare({ unit }) {
  if (!unit) return null;
  const last = (unit.verdicts || [])[(unit.verdicts || []).length - 1];
  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-white p-2" data-testid="uq-compare">
      {last ? (
        <p className="mb-1.5 text-[11px] text-gray-600">
          <span className="font-semibold">Judge:</span> {last.verdict}
          {last.new != null ? ` · new ${last.new}` : ''}
          {last.base != null ? ` vs baseline ${last.base}` : ''}
          {last.evidence ? ` — “${last.evidence}”` : ''}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div>
          <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            Previous version (baseline)
          </div>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-1.5 text-[11px] text-gray-700"
               data-testid="uq-baseline">{unit.baseline || '(no baseline captured)'}</pre>
        </div>
        <div>
          <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            New version
          </div>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-1.5 text-[11px] text-gray-700"
               data-testid="uq-sample">{unit.sample || '(no output captured)'}</pre>
        </div>
      </div>
    </div>
  );
}

export default function RoomAlarmsPanel({ alarms = [], onReset = null, onRefresh = null,
                                          networkId = '', poolId = '' }) {
  const rows = Array.isArray(alarms) ? alarms : [];
  const hasUq = rows.some((a) => String(a.Name || a.name || '').startsWith(UQ_PREFIX));
  const [uqUnits, setUqUnits] = useState({});

  useEffect(() => {
    if (!hasUq || !networkId) return undefined;
    let cancelled = false;
    fetchUpgradeQuality(networkId, poolId)
      .then((data) => {
        if (cancelled) return;
        const by = {};
        for (const u of (Array.isArray(data?.units) ? data.units : [])) by[u.unit] = u;
        setUqUnits(by);
      })
      .catch(() => { /* fail-open — the alarm row still shows its detail */ });
    return () => { cancelled = true; };
  }, [hasUq, networkId, poolId, rows.length]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 p-4" data-testid="room-alarms-panel">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-500" aria-hidden />
        <h3 className="text-sm font-semibold text-gray-800">Degraded ({rows.length})</h3>
        <p className="text-xs text-gray-500">
          Live integration-test alarms for this room. A tripped invariant clears automatically once the
          fix ships and bakes clean; Reset re-arms it now.
        </p>
      </div>
      <div className="flex flex-col gap-3 overflow-auto">
      {groupAlarmsBySegment(rows).map((grp) => (
      <section key={grp.key} data-testid={`room-alarm-group-${grp.key}`}>
      <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        {grp.label} ({grp.rows.length})
      </h4>
      <ul className="flex flex-col gap-2">
        {grp.rows.map((a) => {
          const name = String(a.Name || a.name || '');
          const err = a.LastError || {};
          const detail = String(err.detail || '');
          const trips = Number(a.TripCount || 0);
          const uqUnit = name.startsWith(UQ_PREFIX) ? uqUnits[name.slice(UQ_PREFIX.length)] : null;
          return (
            <li key={name || Math.random()}
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2"
                data-testid="room-alarm-row">
              <div className="flex items-center gap-2">
                <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                  TRIPPED
                </span>
                <span className="font-mono text-[13px] font-medium text-gray-800">{name}</span>
                {trips > 1 ? (
                  <span className="text-[10px] text-red-500">×{trips}</span>
                ) : null}
                {onReset ? (
                  <button
                    type="button"
                    onClick={() => onReset(name)}
                    className="ml-auto inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
                    data-testid="room-alarm-reset"
                    title="Re-arm this alarm for the current release (clears the trip)"
                  >
                    <RotateCcw className="h-3 w-3" aria-hidden />
                    Reset
                  </button>
                ) : null}
              </div>
              {detail ? (
                <p className="mt-1 text-xs text-gray-600">{detail}</p>
              ) : null}
              {err.at ? (
                <p className="mt-0.5 text-[10px] text-gray-400">last seen {_when(err.at)}</p>
              ) : null}
              {uqUnit ? <UpgradeCompare unit={uqUnit} /> : null}
            </li>
          );
        })}
      </ul>
      </section>
      ))}
      </div>
    </div>
  );
}
